import React, { useEffect, useState } from "react";
import api from "../api";
import { AlertCircle, AlertTriangle, Info, Download, Sparkles, FileText, BookOpen, FolderOpen, Mail, Send, X, CheckCircle2 } from "lucide-react";
import { RUBRIQUES } from "../rubriques";

export default function Synthese() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(null);
  const [selectedRubrique, setSelectedRubrique] = useState("");
  const [notaireModalOpen, setNotaireModalOpen] = useState(false);

  useEffect(() => {
    api.get("/synthese").then((r) => setData(r.data)).catch((e) => setError(e?.response?.data?.detail || "Erreur"));
  }, []);

  const triggerDownload = async (url, filename, key) => {
    setDownloading(key);
    try {
      const resp = await api.get(url, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) { console.error(e); }
    finally { setDownloading(null); }
  };

  const today = new Date().toISOString().slice(0, 10);

  const downloadAlertsPdf = () => triggerDownload("/synthese/pdf", `auprea-alertes-${today}.pdf`, "alertes");
  const downloadCompletPdf = () => triggerDownload("/synthese/pdf/complet", `auprea-carnet-complet-${today}.pdf`, "complet");
  const downloadRubriquePdf = () => {
    if (!selectedRubrique) return;
    triggerDownload(`/synthese/pdf/rubrique/${selectedRubrique}`, `auprea-${selectedRubrique}-${today}.pdf`, "rubrique");
  };

  if (error) return <div>{error}</div>;
  if (!data) return <div>Chargement…</div>;

  return (
    <div className="space-y-8">
      <div>
        <div style={{ color: "var(--auprea-gold)", fontSize: 12, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600, marginBottom: 12 }}>
          — Analyse intelligente
        </div>
        <h1 style={{ fontSize: "clamp(2rem, 4vw, 2.75rem)" }} data-testid="synthese-title">Synthèse patrimoniale</h1>
        <p style={{ fontSize: 17, color: "rgba(35,38,46,0.7)", marginTop: 8 }}>
          AUPREA analyse votre carnet et détecte les angles morts successoraux.
        </p>
      </div>

      <div className="auprea-card flex items-center gap-6 flex-wrap">
        <Sparkles size={36} color="#caa86a" />
        <div className="flex-1 min-w-0">
          <div style={{ fontSize: 14, color: "rgba(35,38,46,0.6)", textTransform: "uppercase", letterSpacing: "0.1em" }}>Progression du carnet</div>
          <div className="flex items-baseline gap-4">
            <span style={{ fontFamily: "Spectral, serif", fontSize: 56, fontWeight: 300, color: "var(--auprea-navy)", lineHeight: 1 }} data-testid="synthese-progression">{data.progression}%</span>
          </div>
          <div className="progress-bar-track mt-3" style={{ maxWidth: 480 }}>
            <div className="progress-bar-fill" style={{ width: `${data.progression}%` }} />
          </div>
        </div>
      </div>

      {/* ===== Exports PDF ===== */}
      <div className="auprea-card" data-testid="pdf-export-section">
        <div className="flex items-center gap-3 mb-5">
          <Download size={22} style={{ color: "var(--auprea-gold)" }} />
          <div>
            <h2 style={{ fontSize: 22, margin: 0 }}>Exporter en PDF</h2>
            <p style={{ fontSize: 13, color: "rgba(35,38,46,0.6)", margin: 0 }}>
              3 formats au choix selon votre besoin (conserver, partager au notaire, archive familiale).
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* 1. PDF Alertes rapide */}
          <ExportCard
            icon={<AlertCircle size={20} />}
            title="Synthèse rapide"
            description="Uniquement les alertes successorales et les recommandations détectées."
            buttonLabel={downloading === "alertes" ? "Génération…" : "Télécharger"}
            onClick={downloadAlertsPdf}
            disabled={downloading === "alertes"}
            accent="navy"
            testid="btn-download-alertes"
          />
          {/* 2. PDF complet */}
          <ExportCard
            icon={<BookOpen size={20} />}
            title="Carnet complet"
            description="L'ensemble de votre carnet structuré par rubrique : identité, famille, patrimoine, dernières volontés, alertes."
            buttonLabel={downloading === "complet" ? "Génération…" : "Télécharger"}
            onClick={downloadCompletPdf}
            disabled={downloading === "complet"}
            accent="gold"
            testid="btn-download-complet"
          />
          {/* 3. PDF par rubrique */}
          <ExportCard
            icon={<FolderOpen size={20} />}
            title="Une rubrique précise"
            description="Sélectionnez une seule thématique à exporter (ex. patrimoine immobilier pour votre notaire)."
            accent="green"
            testid="export-rubrique-card"
            content={
              <div className="space-y-2 mt-3">
                <select
                  className="auprea-input"
                  style={{ fontSize: 14 }}
                  value={selectedRubrique}
                  onChange={(e) => setSelectedRubrique(e.target.value)}
                  data-testid="select-rubrique-pdf"
                >
                  <option value="">— Choisir une rubrique —</option>
                  {RUBRIQUES.map((r) => (
                    <option key={r.id} value={r.id}>{r.label}</option>
                  ))}
                </select>
                <button
                  onClick={downloadRubriquePdf}
                  disabled={!selectedRubrique || downloading === "rubrique"}
                  className="auprea-btn w-full"
                  style={{ padding: "8px 14px", minHeight: "auto", fontSize: 14, opacity: !selectedRubrique ? 0.5 : 1 }}
                  data-testid="btn-download-rubrique"
                >
                  <Download size={14} style={{ display: "inline", marginRight: 6 }} />
                  {downloading === "rubrique" ? "Génération…" : "Télécharger"}
                </button>
              </div>
            }
          />
          {/* 4. Envoyer à mon notaire */}
          <ExportCard
            icon={<Mail size={20} />}
            title="Envoyer à mon notaire"
            description="Transmettez votre synthèse par email à votre notaire (ou tout professionnel) directement en pièce jointe."
            buttonLabel="Envoyer"
            onClick={() => setNotaireModalOpen(true)}
            accent="navy-strong"
            testid="btn-send-notaire"
          />
        </div>
      </div>

      {notaireModalOpen && (
        <SendToNotaireModal onClose={() => setNotaireModalOpen(false)} />
      )}

      <div>
        <h2 style={{ fontSize: 26, marginBottom: 16 }}>Alertes détectées ({data.alerts.length})</h2>
        {data.alerts.length === 0 ? (
          <div className="auprea-card text-center" data-testid="alerts-empty">
            <p>Aucune alerte critique. Continuez de remplir votre carnet pour affiner l&apos;analyse.</p>
          </div>
        ) : (
          data.alerts.map((a) => <AlertCard key={a.id} alert={a} />)
        )}
      </div>
    </div>
  );
}

function ExportCard({ icon, title, description, buttonLabel, onClick, disabled, accent = "navy", testid, content }) {
  const colors = {
    navy: { bg: "rgba(21,41,79,0.04)", border: "rgba(21,41,79,0.2)", iconColor: "var(--auprea-navy)" },
    gold: { bg: "rgba(202,168,106,0.08)", border: "rgba(202,168,106,0.4)", iconColor: "#8a6f3d" },
    green: { bg: "rgba(47,125,91,0.06)", border: "rgba(47,125,91,0.3)", iconColor: "var(--auprea-green)" },
    "navy-strong": { bg: "var(--auprea-navy)", border: "var(--auprea-gold)", iconColor: "var(--auprea-gold)" },
  }[accent];
  const isDark = accent === "navy-strong";
  return (
    <div style={{ background: colors.bg, border: `1px solid ${colors.border}`, padding: "18px 18px 16px" }} data-testid={testid}>
      <div style={{ color: colors.iconColor, marginBottom: 10 }}>{icon}</div>
      <h3 style={{ fontSize: 17, fontFamily: "Spectral, serif", color: isDark ? "#f3efe6" : "var(--auprea-navy)", marginBottom: 6 }}>{title}</h3>
      <p style={{ fontSize: 13, color: isDark ? "rgba(243,239,230,0.75)" : "rgba(35,38,46,0.7)", lineHeight: 1.5, marginBottom: content ? 0 : 14 }}>{description}</p>
      {content ? content : (
        <button
          onClick={onClick}
          disabled={disabled}
          className="w-full"
          style={{
            padding: "8px 14px", minHeight: "auto", fontSize: 14, fontWeight: 600,
            background: isDark ? "var(--auprea-gold)" : "var(--auprea-navy)",
            color: isDark ? "var(--auprea-navy)" : "#f3efe6",
            border: "none", borderRadius: 3, cursor: disabled ? "not-allowed" : "pointer",
          }}
          data-testid={`${testid}-button`}
        >
          {isDark ? <Send size={14} style={{ display: "inline", marginRight: 6 }} /> : <Download size={14} style={{ display: "inline", marginRight: 6 }} />}
          {buttonLabel}
        </button>
      )}
    </div>
  );
}

function SendToNotaireModal({ onClose }) {
  const [form, setForm] = useState({ email: "", notaire_nom: "", etude_nom: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [prefilling, setPrefilling] = useState(true);

  // Pré-remplir depuis les données notaire du carnet (rubrique personnes_confiance → notaire_details)
  useEffect(() => {
    api.get("/carnet").then((r) => {
      try {
        const profil = r.data?.profil || {};
        const pc = profil.personnes_confiance || {};
        const nd = pc.notaire_details;
        if (nd && typeof nd === "string") {
          const parsed = nd.startsWith("[") ? JSON.parse(nd)[0] : JSON.parse(nd);
          if (parsed && typeof parsed === "object") {
            setForm((f) => ({
              ...f,
              email: parsed.email || "",
              notaire_nom: parsed.nom || "",
            }));
          }
        }
      } catch (_e) { /* ignore */ }
    }).catch(() => {}).finally(() => setPrefilling(false));
  }, []);

  const isValidEmail = /\S+@\S+\.\S+/.test(form.email);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!isValidEmail || submitting) return;
    setSubmitting(true); setError("");
    try {
      await api.post("/synthese/send-to-notaire", {
        email: form.email.trim(),
        notaire_nom: form.notaire_nom.trim(),
        etude_nom: form.etude_nom.trim(),
        message: form.message.trim(),
      });
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Envoi impossible. Merci de réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(21,41,79,0.55)", zIndex: 60, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      data-testid="notaire-modal"
    >
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", maxWidth: 560, width: "100%", maxHeight: "90vh", overflow: "auto", borderRadius: 6, boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
        <div style={{ background: "var(--auprea-navy)", color: "#f3efe6", padding: "18px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontFamily: "Spectral, serif", fontSize: 20, display: "inline-flex", alignItems: "center", gap: 10 }}>
            <Mail size={20} color="var(--auprea-gold)" /> Envoyer ma synthèse à un notaire
          </div>
          <button onClick={onClose} style={{ color: "#f3efe6", background: "none", border: "none", cursor: "pointer" }} data-testid="notaire-modal-close">
            <X size={22} />
          </button>
        </div>
        <div style={{ padding: 24 }}>
          {sent ? (
            <div style={{ textAlign: "center", padding: "20px 0" }} data-testid="notaire-confirmed">
              <CheckCircle2 size={40} color="var(--auprea-green)" style={{ marginBottom: 12 }} />
              <div style={{ fontFamily: "Spectral, serif", fontSize: 22, color: "var(--auprea-navy)", fontWeight: 500, marginBottom: 8 }}>
                Envoyé !
              </div>
              <p style={{ fontSize: 14, color: "rgba(35,38,46,0.75)", lineHeight: 1.55, maxWidth: 400, margin: "0 auto" }}>
                Votre synthèse patrimoniale a bien été transmise à <strong>{form.email}</strong>.
                Une copie du message reste enregistrée dans votre historique AUPRÉA.
              </p>
              <button onClick={onClose} className="auprea-btn" style={{ marginTop: 18, padding: "10px 24px", minHeight: "auto" }} data-testid="notaire-close-btn">
                Fermer
              </button>
            </div>
          ) : (
            <form onSubmit={submit}>
              <p style={{ fontSize: 13, color: "rgba(35,38,46,0.7)", marginBottom: 16, lineHeight: 1.5 }}>
                Nous générons votre synthèse patrimoniale complète (PDF v2 avec charte AUPRÉA) et l'envoyons directement en pièce jointe au notaire ou professionnel que vous indiquez.
              </p>
              {prefilling && <div style={{ fontSize: 12, color: "rgba(35,38,46,0.5)", fontStyle: "italic", marginBottom: 8 }}>Pré-remplissage depuis votre carnet…</div>}

              <Field label="Email du notaire *" testid="notaire-email" required
                value={form.email} onChange={(v) => setForm((f) => ({ ...f, email: v }))} type="email" placeholder="notaire@etude.fr" />
              <Field label="Nom du notaire (facultatif)" testid="notaire-nom"
                value={form.notaire_nom} onChange={(v) => setForm((f) => ({ ...f, notaire_nom: v }))} placeholder="Ex. Jean Dupont" />
              <Field label="Nom de l'étude (facultatif)" testid="notaire-etude"
                value={form.etude_nom} onChange={(v) => setForm((f) => ({ ...f, etude_nom: v }))} placeholder="Ex. Étude Dupont & Associés" />

              <label style={{ fontSize: 11, color: "rgba(35,38,46,0.7)", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600, display: "block", marginTop: 12, marginBottom: 6 }}>
                Message personnel (facultatif)
              </label>
              <textarea
                className="auprea-input"
                value={form.message}
                onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                placeholder="Ex. Je vous transmets ma synthèse pour préparer notre prochain RDV."
                style={{ width: "100%", minHeight: 90, fontFamily: "inherit" }}
                data-testid="notaire-message"
              />

              {error && (
                <div style={{ marginTop: 12, padding: "10px 14px", background: "rgba(181,58,58,0.08)", border: "1px solid rgba(181,58,58,0.3)", color: "#b53a3a", fontSize: 13, borderRadius: 3 }} data-testid="notaire-error">
                  {error}
                </div>
              )}

              <div style={{ marginTop: 20, display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button type="button" onClick={onClose} className="auprea-btn-outline" style={{ padding: "10px 20px", minHeight: "auto", fontSize: 14 }}>Annuler</button>
                <button
                  type="submit"
                  disabled={!isValidEmail || submitting}
                  className="auprea-btn"
                  style={{ padding: "10px 24px", minHeight: "auto", fontSize: 14, opacity: !isValidEmail || submitting ? 0.5 : 1 }}
                  data-testid="notaire-submit"
                >
                  <Send size={14} style={{ display: "inline", marginRight: 6 }} />
                  {submitting ? "Envoi en cours…" : "Envoyer la synthèse"}
                </button>
              </div>
              <p style={{ marginTop: 16, fontSize: 11, color: "rgba(35,38,46,0.5)", lineHeight: 1.5 }}>
                Le PDF envoyé est identique à celui téléchargeable via « Carnet complet ». Vos coordonnées apparaissent en en-tête ; le message personnel est joint dans le corps de l'email.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, value, onChange, type = "text", placeholder = "", required = false }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ fontSize: 11, color: "rgba(35,38,46,0.7)", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600, display: "block", marginBottom: 6 }}>{label}</label>
      <input
        className="auprea-input"
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        data-testid={testid}
        style={{ width: "100%" }}
      />
    </div>
  );
}

function AlertCard({ alert }) {
  const Icon = alert.level === "danger" ? AlertCircle : alert.level === "warning" ? AlertTriangle : Info;
  const color = alert.level === "danger" ? "#b53a3a" : alert.level === "warning" ? "#caa86a" : "#1d3a72";
  return (
    <div className={`alert-card alert-${alert.level}`} data-testid={`alert-${alert.id}`}>
      <div className="flex items-start gap-4">
        <Icon size={24} color={color} style={{ flexShrink: 0, marginTop: 2 }} />
        <div className="flex-1">
          <h3 style={{ fontSize: 20, marginBottom: 6, color: "var(--auprea-navy)" }}>{alert.title}</h3>
          <p style={{ lineHeight: 1.6, marginBottom: 12 }}>{alert.explanation}</p>
          <div style={{ fontSize: 14, color: "var(--auprea-blue)", padding: "10px 14px", backgroundColor: "rgba(29,58,114,0.06)", borderLeft: "3px solid var(--auprea-blue)" }}>
            <strong>Recommandation : </strong>{alert.recommendation}
          </div>
        </div>
      </div>
    </div>
  );
}
